import type { CompletionStatus, GoalKind, HabitGoal, HabitSchedule, ScheduleKind } from "./types";

/**
 * Calendar helpers for Habits domain. All operations use local day-keys
 * `YYYY-MM-DD`; JS Date objects are only arithmetic vehicles,
 * always at local midday to avoid DST anomalies.
 */

const DAY_KEY_RE = /^\d{4}-\d{2}-\d{2}$/u;
const NOON = 12;

export function isDayKey(value: string): boolean {
  return DAY_KEY_RE.test(value);
}

/** Local day-key of a JS Date (defaults to now). */
export function toDayKey(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Local Date (midday) of a day-key. */
export function fromDayKey(key: string): Date {
  const [y = 0, m = 1, d = 1] = key.split("-").map(Number);
  return new Date(y, m - 1, d, NOON, 0, 0, 0);
}

export function addDays(key: string, days: number): string {
  const date = fromDayKey(key);
  date.setDate(date.getDate() + days);
  return toDayKey(date);
}

/** ISO day of day-key: 1=Monday … 7=Sunday. */
export function isoWeekday(key: string): number {
  const jsDay = fromDayKey(key).getDay(); // 0=Sunday ... 6=Saturday
  return jsDay === 0 ? 7 : jsDay;
}

/** Monday of day-key's week (ISO week start). */
export function weekStart(key: string): string {
  return addDays(key, 1 - isoWeekday(key));
}

/** Days between two day-keys (b - a). */
export function diffDays(a: string, b: string): number {
  const MS_PER_DAY = 86_400_000;
  return Math.round((fromDayKey(b).getTime() - fromDayKey(a).getTime()) / MS_PER_DAY);
}

// ─── Schedule ───────────────────────────────────────────────────────────────

const DEFAULT_TIMES_PER_WEEK = 3;
const ISO_MIN = 1;
const ISO_MAX = 7;

/** CSV "1,3,5" → [1,3,5] saneado (ISO 1–7, sin duplicados, ordenado). */
export function parseWeekdays(csv: string | null | undefined): number[] {
  if (!csv) {
    return [];
  }
  const seen = new Set<number>();
  for (const part of csv.split(",")) {
    const n = Number(part.trim());
    if (Number.isInteger(n) && n >= ISO_MIN && n <= ISO_MAX) {
      seen.add(n);
    }
  }
  return [...seen].toSorted((a, b) => a - b);
}

export function serializeWeekdays(weekdays: number[]): string {
  return parseWeekdays(weekdays.join(",")).join(",");
}

/** Normalizes schedule from SQLite row (individual fields). */
export function parseSchedule(row: {
  schedule_kind: string | null;
  weekdays: string | null;
  times_per_week: number | null;
}): HabitSchedule {
  const kind: ScheduleKind =
    row.schedule_kind === "weekdays" || row.schedule_kind === "times_per_week"
      ? row.schedule_kind
      : "daily";
  const weekdays = parseWeekdays(row.weekdays);
  const timesPerWeek = Math.min(
    ISO_MAX,
    Math.max(ISO_MIN, row.times_per_week ?? DEFAULT_TIMES_PER_WEEK),
  );
  if (kind === "weekdays" && weekdays.length === 0) {
    // Corrupted schedule → fallback to daily (do not drop habit).
    return { kind: "daily", weekdays: [], timesPerWeek };
  }
  return { kind, weekdays, timesPerWeek };
}

/**
 * Is habit "due" on this day?
 * - daily: always.
 * - weekdays: only their ISO days.
 * - times_per_week: any day counts towards weekly target.
 */
export function isScheduledOn(schedule: HabitSchedule, dayKey: string): boolean {
  if (schedule.kind === "weekdays") {
    return schedule.weekdays.includes(isoWeekday(dayKey));
  }
  return true;
}

// ─── Target (binary / quantified) ─────────────────────────────────────────────

const MIN_TARGET = 1;

/** Normalizes target from SQLite row. */
export function parseGoal(row: {
  goal_kind: string | null;
  target_amount: number | null;
  target_unit: string | null;
}): HabitGoal {
  const kind: GoalKind = row.goal_kind === "quantified" ? "quantified" : "binary";
  if (kind === "quantified") {
    return {
      kind,
      targetAmount: Math.max(MIN_TARGET, row.target_amount ?? MIN_TARGET),
      targetUnit: row.target_unit ?? null,
    };
  }
  return { kind: "binary", targetAmount: MIN_TARGET, targetUnit: null };
}

/**
 * EFFECTIVE day status for streak/statistics calculation:
 * - skipped (freeze) is preserved as skipped.
 * - binary: stored `status` presides (row with done status is "done").
 * - quantified: counts as "done" only if progress reaches target;
 *   partial progress (amount < target) does NOT count (same as unmarked day).
 *
 * Returns `null` when day should not count as completed (partial progress).
 */
export function effectiveStatus(
  goal: HabitGoal,
  status: CompletionStatus,
  amount: number | null,
): CompletionStatus | null {
  if (status === "skipped") {
    return "skipped";
  }
  if (goal.kind === "binary") {
    return "done";
  }
  return (amount ?? 0) >= goal.targetAmount ? "done" : null;
}
